/**
 * Valor monetário por extenso (pt-BR) para Recibos.
 *
 * `numeroParaReais(1234.56)` →
 *   "mil duzentos e trinta e quatro reais e cinquenta e seis centavos"
 *
 * Regras de conexão do "e" (padrão do português):
 *  - Dentro de cada classe de 3 dígitos, "e" liga a centena às dezenas/unidades
 *    e as dezenas às unidades:  123 → "cento e vinte e três".
 *  - Entre classes (mil, milhão…), usa-se "e" apenas antes da ÚLTIMA classe não
 *    nula quando ela for menor que 100 OU um múltiplo exato de 100:
 *      1.234 → "mil duzentos e trinta e quatro"   (234 tem centena+dezena → espaço)
 *      1.100 → "mil e cem"                          (100 é múltiplo de 100 → "e")
 *      1.050 → "mil e cinquenta"                    (50 < 100 → "e")
 *  - "mil" nunca vira "um mil".
 *
 * O documento monetário formata reais/centavos com singular/plural:
 *   R$ 1,00 → "um real";  R$ 2,00 → "dois reais";  R$ 0,01 → "um centavo".
 * Quando o valor termina em milhão/bilhão inteiro, usa-se "de reais":
 *   R$ 1.000.000,00 → "um milhão de reais"  (mas "mil reais", sem "de").
 */

const UNITS = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
const TEENS = [
  'dez',
  'onze',
  'doze',
  'treze',
  'quatorze',
  'quinze',
  'dezesseis',
  'dezessete',
  'dezoito',
  'dezenove',
];
const TENS = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const HUNDREDS = [
  '',
  'cento',
  'duzentos',
  'trezentos',
  'quatrocentos',
  'quinhentos',
  'seiscentos',
  'setecentos',
  'oitocentos',
  'novecentos',
];

// Nome da classe (escala) no singular/plural. Índice 0 = unidades simples.
const SCALES: Array<[string, string] | null> = [
  null,
  ['mil', 'mil'],
  ['milhão', 'milhões'],
  ['bilhão', 'bilhões'],
  ['trilhão', 'trilhões'],
];

/** Converte um número de 1..999 em palavras, com "e" interno. */
function tripletToWords(n: number): string {
  if (n === 100) return 'cem';
  const c = Math.floor(n / 100);
  const rest = n % 100;
  const words: string[] = [];
  if (c > 0) words.push(HUNDREDS[c]);
  if (rest > 0) {
    if (rest < 10) {
      words.push(UNITS[rest]);
    } else if (rest < 20) {
      words.push(TEENS[rest - 10]);
    } else {
      const t = Math.floor(rest / 10);
      const u = rest % 10;
      words.push(u > 0 ? `${TENS[t]} e ${UNITS[u]}` : TENS[t]);
    }
  }
  return words.join(' e ');
}

/** Palavra da escala para a classe `i` com valor de grupo `g` (singular/plural). */
function scaleWord(i: number, g: number): string {
  const pair = SCALES[i];
  if (!pair) return '';
  return g === 1 ? pair[0] : pair[1];
}

/**
 * Converte um inteiro não-negativo em palavras (sem "reais"). Suporta até a
 * casa dos trilhões — muito além do teto de numeric(14,2) usado no recibo.
 */
export function inteiroPorExtenso(intPart: number): string {
  const n = Math.floor(Math.abs(intPart));
  if (n === 0) return 'zero';

  // Quebra em grupos de 3 dígitos (baixo → alto): groups[0] = unidades simples.
  const groups: number[] = [];
  let rest = n;
  while (rest > 0) {
    groups.push(rest % 1000);
    rest = Math.floor(rest / 1000);
  }

  // Monta os trechos falados do mais significativo para o menos.
  const spoken: Array<{ value: number; scaleIndex: number; text: string }> = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i];
    if (g === 0) continue;
    let text: string;
    if (i === 1 && g === 1) {
      text = 'mil'; // nunca "um mil"
    } else {
      text = tripletToWords(g);
      const scale = scaleWord(i, g);
      if (scale) text += ` ${scale}`;
    }
    spoken.push({ value: g, scaleIndex: i, text });
  }

  // Liga os trechos: espaço entre classes, "e" apenas antes da última classe
  // não nula quando ela for < 100 ou múltiplo de 100.
  let result = spoken[0].text;
  for (let k = 1; k < spoken.length; k++) {
    const lower = spoken[k];
    const isLast = k === spoken.length - 1;
    const useE = isLast && (lower.value < 100 || lower.value % 100 === 0);
    result += (useE ? ' e ' : ' ') + lower.text;
  }
  return result;
}

/**
 * Valor monetário por extenso em reais/centavos. Arredonda para 2 casas.
 * Negativos são prefixados com "menos" (não esperado num recibo, mas seguro).
 */
export function numeroParaReais(value: number): string {
  const safe = Number.isFinite(value) ? value : 0;
  const negative = safe < 0;
  const abs = Math.abs(safe);

  // Arredonda em centavos com uma folga de epsilon para evitar erros de ponto
  // flutuante (ex.: 1.005 * 100 = 100.4999…).
  const totalCents = Math.round(abs * 100 + Number.EPSILON);
  const reais = Math.floor(totalCents / 100);
  const centavos = totalCents % 100;

  const parts: string[] = [];
  if (reais > 0) {
    // "de reais" quando o valor termina em milhão/bilhão inteiro (não para "mil").
    const usesDe = reais >= 1_000_000 && reais % 1_000_000 === 0;
    const reaisWord = reais === 1 ? 'real' : 'reais';
    parts.push(`${inteiroPorExtenso(reais)} ${usesDe ? 'de ' : ''}${reaisWord}`);
  }
  if (centavos > 0) {
    parts.push(`${inteiroPorExtenso(centavos)} ${centavos === 1 ? 'centavo' : 'centavos'}`);
  }

  const text = parts.length === 0 ? 'zero reais' : parts.join(' e ');
  return negative ? `menos ${text}` : text;
}
